import {Component, Input} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {IBaseCRUModalComponent} from '../../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {SdkImportsModule} from '../../../../../sdk/src/sdk-imports';
import {FileEntryModelExtension} from '../../../api-extensions/models/file-entry.model-extension';
import {CpmCFileUpdateUserFileApiSchema} from '../../../../../gen/cpm-c-api-ts/src/schemas/c-common/cpmc-file.api-schemas';
import {CpmCFileApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-file.api-service';
import {firstValueFrom, Observable} from 'rxjs';
import {TreeNode} from 'primeng/api';
import {ECpmCFileEntryTypeApiEnum} from '../../../../../gen/cpm-c-api-ts/src/enums/c-common/cpmc-file-entry.api-enums';

export class MoveFileModalConfig {
    constructor(
        public fileEntry: FileEntryModelExtension,
        public currentFolderUUID?: string
    ) {}
}

export class MoveFileModalResult {
    constructor(public item?: FileEntryModelExtension) {}
}

/**
 * Move file/folder modal
 */
@Component({
    selector: 'app-move-file-modal',
    templateUrl: './move-file-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class MoveFileModalComponent extends IBaseCRUModalComponent<
    FileEntryModelExtension,
    MoveFileModalComponent,
    MoveFileModalConfig,
    MoveFileModalResult
> {
    @Input()
    public fileEntry?: FileEntryModelExtension;

    @Input()
    public currentFolderUUID?: string;

    protected folderTree: TreeNode<FileEntryModelExtension>[] = [];
    protected selectedFolder: TreeNode<FileEntryModelExtension> | null = null;

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure('Move File', (model: FileEntryModelExtension) => model.name ?? '<Unknown file>');
        this.config.width = '40rem';
        this.contentMinHeight = '20rem';

        // Custom validation: require folder selection
        this.canSubmitExtra = () => this.selectedFolder !== null;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            destination: new FormControl<TreeNode<FileEntryModelExtension> | null>(null)
        });

        if (this.config.data?.extended?.fileEntry) this.fileEntry = this.config.data.extended.fileEntry;
        if (this.config.data?.extended?.currentFolderUUID)
            this.currentFolderUUID = this.config.data.extended.currentFolderUUID;

        await this.loadFolderTree();
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Load all folders and build tree structure
     */
    private async loadFolderTree(): Promise<void> {
        this.loadingStr = 'Loading folders...';

        await this.asyncCritical(async () => {
            // Fetch ALL directories recursively by querying without parent filter
            // This will return all user folders regardless of nesting level
            const allFolders: FileEntryModelExtension[] = [];

            // First, get root level folders
            const rootResult = await firstValueFrom(
                CpmCFileApiService.getUserFileList({
                    is_dir: true,
                    type: ECpmCFileEntryTypeApiEnum.USER,
                    parent_path: '',
                    limit: 10000
                })
            );

            allFolders.push(...(rootResult.data || []));

            // Then recursively load subfolders for each folder found
            await this.loadSubfoldersRecursively(allFolders, rootResult.data || []);

            // Build tree structure from all collected folders
            this.folderTree = this.buildFolderTree(allFolders);
        });
    }

    /**
     * Recursively load subfolders for given parent folders
     */
    private async loadSubfoldersRecursively(
        allFolders: FileEntryModelExtension[],
        parentFolders: FileEntryModelExtension[]
    ): Promise<void> {
        for (const folder of parentFolders) {
            await this.asyncCritical(async () => {
                const subResult = await firstValueFrom(
                    CpmCFileApiService.getUserFileList({
                        is_dir: true,
                        type: ECpmCFileEntryTypeApiEnum.USER,
                        parent_uuid: folder.uuid,
                        limit: 10000
                    })
                );

                const subfolders = subResult.data || [];
                if (subfolders.length > 0) {
                    allFolders.push(...subfolders);
                    // Recursively load their subfolders
                    await this.loadSubfoldersRecursively(allFolders, subfolders);
                }
            });
        }
    }

    /**
     * Build tree structure from flat folder list
     */
    private buildFolderTree(folders: FileEntryModelExtension[]): TreeNode<FileEntryModelExtension>[] {
        const nodeMap = new Map<string, TreeNode<FileEntryModelExtension>>();
        const roots: TreeNode<FileEntryModelExtension>[] = [];

        // Create root node (My Files)
        const rootNode: TreeNode<FileEntryModelExtension> = {
            key: 'root',
            label: 'My Files',
            data: undefined,
            children: [],
            expanded: true,
            selectable: true
        };
        nodeMap.set('root', rootNode);
        roots.push(rootNode);

        // Create tree nodes for all folders
        folders.forEach((folder) => {
            const node: TreeNode<FileEntryModelExtension> = {
                key: folder.uuid,
                label: folder.name || '<Unnamed>',
                data: folder,
                children: [],
                expanded: false,
                selectable: !this.isCircularMove(folder.uuid)
            };
            nodeMap.set(folder.uuid!, node);
        });

        // Build parent-child relationships
        folders.forEach((folder) => {
            const node = nodeMap.get(folder.uuid!);
            if (!node) return;

            if (!folder.parent_uuid) {
                // Top-level folder - add to root
                rootNode.children!.push(node);
            } else {
                // Child folder - find parent
                const parentNode = nodeMap.get(folder.parent_uuid);
                if (parentNode) {
                    parentNode.children!.push(node);
                } else {
                    // Parent not found, add to root
                    rootNode.children!.push(node);
                }
            }
        });

        return roots;
    }

    /**
     * Check if moving to this folder would create a circular reference
     */
    private isCircularMove(targetFolderUUID: string): boolean {
        if (!this.fileEntry?.is_dir) {
            // Moving a file - no circular move possible
            return false;
        }

        // Cannot move folder into itself
        if (targetFolderUUID === this.fileEntry.uuid) {
            return true;
        }

        // Cannot move folder into one of its descendants
        return this.isDescendant(targetFolderUUID, this.fileEntry.uuid!);
    }

    /**
     * Check if potentialDescendant is a descendant of ancestor
     */
    private isDescendant(potentialDescendantUUID: string, ancestorUUID: string): boolean {
        const nodeMap = new Map<string, FileEntryModelExtension>();
        const collectNodes = (nodes: TreeNode<FileEntryModelExtension>[]) => {
            nodes.forEach((node) => {
                if (node.data?.uuid) {
                    nodeMap.set(node.data.uuid, node.data);
                }
                if (node.children) {
                    collectNodes(node.children);
                }
            });
        };
        collectNodes(this.folderTree);

        let current = nodeMap.get(potentialDescendantUUID);
        while (current?.parent_uuid) {
            if (current.parent_uuid === ancestorUUID) {
                return true;
            }
            current = nodeMap.get(current.parent_uuid);
        }
        return false;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On tree node selection changed
     */
    protected onNodeSelect(event: {node: TreeNode<FileEntryModelExtension>}): void {
        this.selectedFolder = event.node;
    }

    /**
     * On tree node unselection
     */
    protected onNodeUnselect(): void {
        this.selectedFolder = null;
    }

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        await this.onSubmitConfirmed();
    }

    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit() || !this.fileEntry || !this.selectedFolder) {
            return;
        }

        const newParentUUID = this.selectedFolder.key === 'root' ? null : this.selectedFolder.data?.uuid;

        // Backend validation requires at least one field to be non-null
        // When moving to root (new_parent_uuid: null), also send current name to satisfy validation
        const toSubmit: CpmCFileUpdateUserFileApiSchema = {
            new_name: this.fileEntry.name, // Keep same name to satisfy backend validation
            new_parent_uuid: newParentUUID ?? null
        };

        await this.submitCU(
            null,
            (payload: CpmCFileUpdateUserFileApiSchema) =>
                CpmCFileApiService.patchUpdateUserFile(this.fileEntry!.uuid, payload),
            this.dummyPatchMoveFile.bind(this),
            undefined,
            toSubmit,
            (resultCRUD: FileEntryModelExtension) => new MoveFileModalResult(resultCRUD)
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * Dummy proxy method
     */
    private dummyPatchMoveFile(
        uuid: string,
        payload: CpmCFileUpdateUserFileApiSchema
    ): Observable<FileEntryModelExtension> {
        return CpmCFileApiService.patchUpdateUserFile(this.fileEntry!.uuid, payload);
    }
}
